import React, { useState, useEffect } from 'react';
import { PencilIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import { collection, getDocs, deleteDoc, doc, updateDoc, addDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import AddVehicleModal from './AddVehicleModal';
import ConfirmationDialog from '../common/ConfirmationDialog';
import DataTable from '../common/DataTable';

export default function VehiclesTab() {
  const [vehicles, setVehicles] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState(null);

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      const vehiclesRef = collection(db, 'vehicles');
      const snapshot = await getDocs(vehiclesRef);
      const vehiclesList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setVehicles(vehiclesList);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    }
  };

  const handleSubmit = async (formData) => {
    try {
      if (editingVehicle) {
        await updateDoc(doc(db, 'vehicles', editingVehicle.id), formData);
      } else {
        await addDoc(collection(db, 'vehicles'), formData);
      }
      fetchVehicles();
      setIsModalOpen(false);
      setEditingVehicle(null);
    } catch (error) {
      console.error('Error saving vehicle:', error);
      throw error;
    }
  };

  const handleDelete = async () => {
    try {
      await deleteDoc(doc(db, 'vehicles', vehicleToDelete.id));
      fetchVehicles();
      setDeleteDialogOpen(false);
      setVehicleToDelete(null);
    } catch (error) {
      console.error('Error deleting vehicle:', error);
    }
  };

  const columns = [
    {
      header: 'Registration',
      accessorKey: 'registrationNumber',
    },
    {
      header: 'Vehicle Type',
      accessorKey: 'vehicleType',
    },
    {
      header: 'Driver',
      accessorKey: 'driverName',
    },
    {
      header: 'Contact',
      accessorKey: 'driverPhone',
    },
    {
      header: 'Actions',
      cell: info => (
        <div className="flex space-x-2">
          <button
            onClick={() => {
              setEditingVehicle(info.row.original);
              setIsModalOpen(true);
            }}
            className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
          >
            <PencilIcon className="h-5 w-5" />
          </button>
          <button
            onClick={() => {
              setVehicleToDelete(info.row.original);
              setDeleteDialogOpen(true);
            }}
            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
          >
            <TrashIcon className="h-5 w-5" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div>
        <div className="flex justify-end mb-4">
          <button 
            onClick={() => setIsModalOpen(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium 
              rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 
              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Vehicle
          </button>
        </div>

        <DataTable
          data={vehicles}
          columns={columns}
        />
      </div>

      <AddVehicleModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingVehicle(null);
        }}
        vehicle={editingVehicle}
        onSubmit={handleSubmit}
      />

      <ConfirmationDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title="Delete Vehicle"
        message={`Are you sure you want to delete ${vehicleToDelete?.registrationNumber}?`}
      />
    </>
  );
}